/** Server-side status filter values for /api/v1/alert. */
export type AlertStatus = 'FIRING' | 'RESOLVED';

export interface AlertRule {
  id: string;
  name: string;
  /** Enum value from the /options vocabulary (e.g. BATTERY_CAPACITY). */
  metric: string;
  /** Enum value from the /options vocabulary (e.g. LT). Null for OFFLINE. */
  operator: string | null;
  /** Null for OFFLINE. */
  threshold: number | null;
  enabled: boolean;
  created: string;
  lastUpdated: string;
}

/** POST/PUT body — a full representation, PUT is a full update. */
export interface AlertRuleRequest {
  name: string;
  metric: string;
  operator: string | null;
  threshold: number | null;
  enabled: boolean;
}

export interface AlertMetricOption {
  value: string;
  unit: string;
  /**
   * False for condition-less metrics (OFFLINE): they carry no operator or
   * threshold (the backend derives the heartbeat window per device from its
   * power profile), so the rule builder swaps the condition inputs for a
   * static hint and condition summaries render a fixed text.
   */
  thresholdBased: boolean;
}

export interface AlertOperatorOption {
  value: string;
}

/**
 * Rule-builder vocabulary from GET /api/v1/alert-rule/options. Dropdowns are
 * populated from this — metric/operator values are never hardcoded, because an
 * invalid enum value comes back as a framework-shaped 400 (not the envelope).
 * Options carry only enum values; display labels are client-side translations
 * resolved via `optionLabel`.
 */
export interface AlertRuleOptions {
  metrics: AlertMetricOption[];
  operators: AlertOperatorOption[];
}

/**
 * Localized display label for a metric/operator enum value, keyed as
 * `alerts.options.<kind>.<VALUE>`. An enum value without a translation (the
 * backend vocabulary can run ahead of the frontend) falls back to the raw
 * value — Transloco returns the key itself for missing entries.
 */
export function optionLabel(
  translate: (key: string) => string,
  kind: 'metrics' | 'operators' | 'channels',
  value: string,
): string {
  const key = `alerts.options.${kind}.${value}`;
  const label = translate(key);
  return label === key ? value : label;
}

/**
 * A notification channel's delivery health, from GET /alert/delivery-status.
 * One entry per channel that has attempted a delivery; `lastError` is the
 * currently outstanding problem and clears on the channel's next successful
 * send (history lives in the alert log, not here).
 */
export interface AlertDeliveryStatus {
  /** Enum value (e.g. WEBHOOK) — display label via optionLabel('channels'). */
  channel: string;
  lastError: string | null;
  lastErrorAt: string | null;
  lastSuccessAt: string | null;
}

/** Failing while an error is outstanding — the backend clears it on success. */
export function isDeliveryFailing(status: AlertDeliveryStatus): boolean {
  return status.lastError != null;
}

export interface Alert {
  id: string;
  /** "RULE" today; more types later. */
  type: string;
  /** May reference a deleted rule — fall back to `detail` when unresolvable. */
  ruleId: string | null;
  /** Device id as a string. */
  subject: string;
  /** Human-readable condition, e.g. "Battery capacity 14.0 % < 20.0 %". */
  detail: string;
  status: AlertStatus;
  startedAt: string;
  resolvedAt: string | null;
}

/**
 * Display symbols for the comparison operators, used to compose compact
 * condition summaries ("Battery capacity < 20 %"). Presentation only — the
 * source of truth for selectable operators is /options; an unknown operator
 * value falls back to itself.
 */
const OPERATOR_SYMBOLS: Record<string, string> = {
  GT: '>',
  GTE: '>=',
  LT: '<',
  LTE: '<=',
  EQ: '=',
  NEQ: '!=',
};

export function operatorSymbol(operator: string): string {
  return OPERATOR_SYMBOLS[operator] ?? operator;
}
